import React from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { authFormBlur } from '../../../utils/auth-form-navigation';
import { SignUpUserMobxDto } from '../../../models/mobx/dtos/auth/sign-up-user-mobx-dto';
import { FormItem } from '../../app/form/item/item';
import { TextField } from '../../app/form/textfield/textfield';
import { WebButton as Button } from '../button/web-button';
import { useRootStore } from '../../../providers/root-store-provider';
import { AgreeTerms } from '../dialogs/account/agree/terms/agree-terms';
import { Agree } from '../dialogs/account/agree/agree';
import { PasswordField } from '../../app/form/textfield/password-field';
import { ConsentRequiredAction } from '../dialogs/account/consent-required-action';
import { AgreeMarketing } from '../dialogs/account/agree/marketing/agree-marketing';
import { PasswordRequirements } from './password-requirements';
import { PasswordConfirmation } from './password-confirmation';
import {
    PASSWORD_MIN_LENGTH,
    PASSWORD_MAX_LENGTH,
    PASSWORD_CHARACTER_PATTERN,
} from '../../../utils/password-requirements';

export interface SignUpFormPropsInterface {
    onSubmit: (values: SignUpUserMobxDto) => void;
    loading?: boolean;
    requireFullName?: boolean;
}

export const SignUpForm: React.FC<SignUpFormPropsInterface> = observer((props) => {
    const { t } = useTranslation();
    const { onSubmit, loading, requireFullName = false } = props;
    const {
        authStore: { authDialogStore },
        alertStore,
    } = useRootStore();

    const initialValues = new SignUpUserMobxDto();
    const validationSchema = yup.object({
        fullName: requireFullName ? yup.string().required(t('layout.forms.common.nameValidation')) : yup.string(),
        email: yup
            .string()
            .required(t('layout.forms.common.emailValidationRequired'))
            .email(t('layout.forms.common.emailValidationValid'))
            .matches(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/, t('layout.forms.common.emailValidationValid')),
        password: yup
            .string()
            .required(t('layout.forms.common.passwordValidation.required'))
            .min(PASSWORD_MIN_LENGTH, t('layout.forms.common.passwordValidation.tooShort'))
            .max(PASSWORD_MAX_LENGTH, t('layout.forms.common.passwordValidation.tooLong'))
            .matches(PASSWORD_CHARACTER_PATTERN, t('layout.forms.common.passwordValidation.valid')),
        passwordConfirm: yup
            .string()
            .required(t('layout.forms.common.passwordConfirmValidation'))
            .oneOf([yup.ref('password'), null], t('layout.forms.common.passwordConfirmValidationEquality')),
    });

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
            {({ values, handleChange, touched, handleBlur, errors, handleSubmit }) => (
                <Form id={'signup-form'}>
                    <div className="grid gap-vsmall">
                        {requireFullName && (
                            <FormItem gapBottom="none">
                                <TextField
                                    appearance="web"
                                    fullWidth
                                    label={t('layout.forms.common.nameField')}
                                    name="fullName"
                                    value={values.fullName}
                                    onChange={handleChange}
                                    onBlur={authFormBlur(handleBlur)}
                                    helperText={touched.fullName && errors.fullName ? errors.fullName : ''}
                                    error={Boolean(touched.fullName && errors.fullName)}
                                />
                            </FormItem>
                        )}
                        <FormItem gapBottom="none">
                            <TextField
                                appearance="web"
                                fullWidth
                                label={t('layout.forms.common.emailField')}
                                name="email"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={authFormBlur(handleBlur)}
                                helperText={touched.email && errors.email ? errors.email : ''}
                                error={Boolean(touched.email && errors.email)}
                            />
                        </FormItem>
                        <FormItem gapBottom="none">
                            <>
                                <PasswordField
                                    appearance="web"
                                    fullWidth
                                    name="password"
                                    suffix={<PasswordRequirements password={values.password} />}
                                    label={t('layout.forms.common.passwordField')}
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={authFormBlur(handleBlur)}
                                    helperText={touched.password && errors.password ? errors.password : ''}
                                    error={Boolean(touched.password && errors.password)}
                                />
                                <PasswordConfirmation
                                    password={values.password}
                                    value={values.passwordConfirm}
                                    onChange={handleChange}
                                    onBlur={authFormBlur(handleBlur)}
                                    helperText={
                                        touched.passwordConfirm && errors.passwordConfirm ? errors.passwordConfirm : ''
                                    }
                                    error={Boolean(touched.passwordConfirm && errors.passwordConfirm)}
                                />
                            </>
                        </FormItem>
                    </div>
                    <FormItem>
                        <Agree>
                            <AgreeTerms handleChange={handleChange} />
                            <AgreeMarketing handleChange={handleChange} />
                        </Agree>
                    </FormItem>
                    <FormItem>
                        {!authDialogStore.agreedTerms ? (
                            <ConsentRequiredAction>
                                <Button
                                    size="large"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (authDialogStore.agreedTerms) {
                                            handleSubmit();
                                        } else {
                                            alertStore.setErrorMessage(t('layout.forms.common.agreeTermsValidation'));
                                        }
                                    }}
                                    block
                                    variant="primary"
                                    loading={loading}
                                    disabled
                                >
                                    {t('layout.forms.signUp.register')}
                                </Button>
                            </ConsentRequiredAction>
                        ) : (
                            <Button
                                size="large"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (authDialogStore.agreedTerms) {
                                        handleSubmit();
                                    } else {
                                        alertStore.setErrorMessage(t('layout.forms.common.agreeTermsValidation'));
                                    }
                                }}
                                block
                                variant="primary"
                                loading={loading}
                            >
                                {t('layout.forms.signUp.register')}
                            </Button>
                        )}
                    </FormItem>
                </Form>
            )}
        </Formik>
    );
});
